import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, ArrowLeft, Share2, CheckCircle2, ExternalLink, Copy, Loader2, Globe, Instagram } from "lucide-react";
import { toast } from "sonner";
import FbGroupShare from "@/components/FbGroupShare";

export default function AdminFbPostShare() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/fb-posts/${id}`); setP(data); }
    catch (e) { toast.error("Publication introuvable."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18}/> Chargement…</div>;
  if (!p) return <div className="p-10 text-slate-500">Publication introuvable. <Link to="/admin/publications-facebook" className="text-[#E05638] font-semibold">Retour</Link></div>;

  const media = p.media || [];
  const images = media.filter((m) => m.type === "image");
  const videos = media.filter((m) => m.type === "video");
  if (p.image_url && images.length === 0) images.push({ url: p.image_url });

  const shareUrl = p.public_share_url || p.fb_post_url || p.destination_url || (typeof window !== "undefined" ? window.location.origin : "");
  const text = p.composed_message || [p.title, p.text].filter(Boolean).join("\n\n");
  const enc = encodeURIComponent;
  const combined = [text, shareUrl].filter(Boolean).join("\n");
  const links = {
    wa: `https://wa.me/?text=${enc(combined)}`,
    mail: `mailto:?subject=${enc(p.title || "Publication PROXIreduc-28")}&body=${enc(combined)}`,
    fb: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(text)}`,
    x: `https://twitter.com/intent/tweet?text=${enc(text)}${shareUrl ? `&url=${enc(shareUrl)}` : ""}`,
  };
  const copy = async () => { try { await navigator.clipboard.writeText(combined); toast.success("Contenu copié !"); } catch { toast.message("Copie manuelle requise."); } };
  const facebook = (e) => {
    // Copie non bloquante du texte + lien (on reste dans le geste utilisateur)
    try { if (navigator.clipboard) navigator.clipboard.writeText(combined); } catch (_) { /* noop */ }
    // Mobile : partage natif qui transmet le TEXTE à l'app Facebook
    if (navigator.share) {
      if (e) e.preventDefault();
      navigator.share({ title: p.title || "PROXIreduc-28", text: combined })
        .catch(() => window.open(links.fb, "_blank", "noopener"));
      return;
    }
    // Desktop : on LAISSE l'ancre ouvrir Facebook (pas de window.open après await → plus de blocage)
    toast.success("Texte copié ! Dans Facebook, faites Coller (Ctrl/Cmd+V) : votre texte apparaîtra au-dessus de l'aperçu.", { duration: 7000 });
  };
  const native = async () => { if (navigator.share) { try { await navigator.share({ title: p.title, text: combined, url: shareUrl || undefined }); } catch (e) { /* noop */ } } else copy(); };
  const instagram = () => {
    try { if (navigator.clipboard) navigator.clipboard.writeText(combined); } catch (_) { /* noop */ }
    toast.success("Texte copié ! Collez-le dans votre publication Instagram.");
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/fb-posts/${id}`, { action: "validate" });
      if (data.published_to_facebook) toast.success("Validée et publiée sur la page Facebook ✅");
      else toast.warning("Validée. Publication Facebook auto non effectuée : " + (data.facebook_error || "page non connectée") + ". Utilisez le partage ci-dessous.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  const canPublish = ["en_attente", "a_modifier", "validee", "programmee"].includes(p.status);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="admin-fbpost-share-page">
      <Link to="/admin/publications-facebook" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={14}/> Retour aux publications
      </Link>
      <h1 className="font-heading font-extrabold text-2xl text-slate-900">Aperçu & partage de la publication</h1>
      <p className="text-sm text-slate-500 mt-1">Voici la publication telle qu'elle sera partagée. Publiez-la sur votre page ou partagez-la en un clic.</p>

      {/* Aperçu façon Facebook */}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="fbpost-preview">
        <div className="flex items-center gap-3 p-4">
          <div className="w-11 h-11 rounded-full bg-[#1877F2] grid place-items-center text-white"><Facebook size={22}/></div>
          <div>
            <div className="font-bold text-slate-900 leading-tight">PROXIreduc-28</div>
            <div className="text-xs text-slate-400">{p.business_name ? `via ${p.business_name} · ` : ""}À l'instant · 🌍</div>
          </div>
        </div>
        {text && <div className="px-4 pb-3 whitespace-pre-wrap text-[15px] text-slate-800">{text}</div>}
        {videos.length > 0 && <video src={videos[0].url} controls className="w-full max-h-96 bg-black"/>}
        {images.length > 0 && (
          <div className={`grid ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-0.5`}>
            {images.slice(0, 4).map((m, i) => <img key={i} src={m.url} alt="" className="w-full aspect-[1.91/1] object-contain bg-slate-100"/>)}
          </div>
        )}
        {p.destination_url && (
          <a href={p.destination_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-t border-slate-100 text-sm text-slate-600 hover:bg-slate-100">
            <Globe size={15}/> <span className="truncate">{p.destination_url}</span>
          </a>
        )}
      </div>

      {/* Statut + publication */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="fbpost-status">{p.status}</span>
        {p.status === "publiee" && p.fb_post_url && (
          <a href={p.fb_post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700 font-semibold" data-testid="fbpost-fb-link">
            Voir sur Facebook <ExternalLink size={13}/>
          </a>
        )}
        {canPublish && (
          <Button onClick={publish} disabled={busy} className="btn-shine text-white" data-testid="fbpost-publish-btn">
            {busy ? <Loader2 size={15} className="mr-1 animate-spin"/> : <CheckCircle2 size={15} className="mr-1"/>}
            Valider & publier sur Facebook
          </Button>
        )}
      </div>

      {/* Partage */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="fbpost-share-box">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Share2 size={18} className="text-[#E05638]"/> Partager cette publication</div>
        <p className="text-xs text-slate-500 mt-1">Diffusez-la sur vos réseaux, par email ou WhatsApp.</p>
        <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="fbpost-share-tip">
          <Facebook size={14} className="mt-0.5 shrink-0"/>
          <span>Facebook n'autorise pas le pré-remplissage automatique du texte. Le bouton <b>Facebook</b> copie votre texte : il suffit de faire <b>Coller (Ctrl/Cmd+V)</b> dans la fenêtre Facebook. Sur mobile, le texte est transmis automatiquement.</span>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button onClick={copy} className="col-span-2 sm:col-span-3 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E05638] text-white font-bold hover:opacity-90" data-testid="fbpost-share-copy-text"><Copy size={16}/> Copier le texte + le lien</button>
          <a href={links.wa} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-50 text-green-700 font-semibold hover:bg-green-100" data-testid="fbpost-share-wa">WhatsApp</a>
          <a href={links.mail} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200" data-testid="fbpost-share-mail">Email</a>
          {links.fb && <a href={links.fb} target="_blank" rel="noopener noreferrer" onClick={facebook} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 cursor-pointer" data-testid="fbpost-share-fb"><Facebook size={15}/> Facebook</a>}
          <button onClick={instagram} className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-white hover:opacity-90" style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }} data-testid="fbpost-share-ig"><Instagram size={15}/> Instagram</button>
          <a href={links.x} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:opacity-90" data-testid="fbpost-share-x">X / Twitter</a>
          <button onClick={copy} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200" data-testid="fbpost-share-copy"><Copy size={15}/> Copier</button>
          <button onClick={native} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#E05638] text-white font-semibold hover:opacity-90" data-testid="fbpost-share-native"><Share2 size={15}/> Autre…</button>
        </div>
      </div>

      <FbGroupShare text={text} shareUrl={shareUrl} />
    </div>
  );
}
